<template>
    <v-container grid-list-md pa-0 relative>
        <v-layout row wrap>
            <v-flex xs12>
                <v-card outlined class="primary--border">
                    <v-card-title class="title">
                        Activity Log
                    </v-card-title>

                    <v-card outlined>
                        <v-card-title class="title mt-3">
                            <v-flex xs12 sm3 md2>
                                <v-calendar-field id-val="from_date" v-model="frmDate" label="From Date" />
                            </v-flex>

                            <v-flex xs12 sm3 md2>
                                <v-calendar-field id-val="to_date" v-model="toDate" label="To Date" />
                            </v-flex>

                            <v-flex xs12 sm3 md3>
                                <v-text-field v-model="search" label="Search" outlined dense clearable hide-details
                                    @keyup.enter="searchLogs" />
                            </v-flex>

                            <v-flex xs12 sm2 md1>
                                <search-button :disabled="loading" permission="billing-report-collection"
                                    @action="searchLogs">
                                    Search
                                </search-button>
                            </v-flex>
                        </v-card-title>

                        <div v-if="records.length" class="data-represent" style="margin-top: -10px;">
                            <div>
                                <span class="d-info"></span>
                                &nbsp;

                                <small>
                                    Showing
                                    <strong>{{ records.length }}</strong>
                                    of
                                    <strong>{{ totalItems }}</strong>
                                    activity(s)
                                </small>
                            </div>

                            <div v-if="failedCount">
                                <span class="d-error"></span>
                                &nbsp;

                                <small>
                                    Failed:
                                    <strong>{{ failedCount }}</strong>
                                </small>
                            </div>
                        </div>

                        <br />
                    </v-card>

                    <v-data-table :headers="headers" :items="records" :options.sync="pagination" :footer-props="{
                        ...footerProps,
                        itemsPerPageOptions: [10, 25, 50, 100]
                    }" :server-items-length="totalItems" :loading="loading" class="activity-log-table">
                        <template v-slot:item="{ index, item }">
                            <tr :class="{ 'failed-row': !item.success }">
                                <td class="text-center">
                                    {{ rowNumber(index) }}
                                </td>

                                <td class="text-left">
                                    <strong>{{ item.created_at_bs || "-" }}</strong>

                                    <small v-if="formatTime(item.created_at)" class="cell-subtitle">
                                        {{ formatTime(item.created_at) }}
                                    </small>
                                </td>

                                <td class="text-left">
                                    <strong>{{ trim(item.user_name) || "-" }}</strong>

                                    <small v-if="item.user_role" class="cell-subtitle">
                                        {{ item.user_role }}
                                    </small>
                                </td>

                                <td class="text-left">
                                    <v-chip x-small label :color="actionColor(item.action)" text-color="white">
                                        {{ formatAction(item.action) }}
                                    </v-chip>
                                </td>

                                <td class="text-left">
                                    <strong>{{ item.entity_type || "-" }}</strong>

                                    <small v-if="entityIds(item).length" class="cell-subtitle">
                                        {{ entityIds(item)[0] }}
                                        <template v-if="entityIds(item).length > 1">
                                            +{{ entityIds(item).length - 1 }} more
                                        </template>
                                    </small>
                                </td>

                                <td class="text-right">
                                    {{ item.amount === null ? "-" : currency(item.amount) }}
                                </td>

                                <td class="text-center">
                                    <v-chip x-small label :color="item.success ? 'success' : 'error'"
                                        text-color="white">
                                        {{ item.success ? "Success" : "Failed" }}
                                    </v-chip>

                                    <small v-if="item.status_code" class="cell-subtitle">
                                        {{ item.status_code }}
                                    </small>
                                </td>

                                <td class="text-left log-remarks">
                                    {{ remarks(item) || "-" }}
                                </td>

                                <td class="text-left">
                                    {{ item.ip_address || "-" }}
                                </td>

                                <td class="text-center">
                                    <v-btn small icon color="primary" class="pa-0 ma-1" title="View Details"
                                        @click="viewDetails(item)">
                                        <v-icon dark>visibility</v-icon>
                                    </v-btn>
                                </td>
                            </tr>
                        </template>

                        <template v-slot:no-data>
                            <div class="pa-8 text-center grey--text">
                                No activity found for the selected filters.
                            </div>
                        </template>
                    </v-data-table>
                </v-card>
            </v-flex>
        </v-layout>

        <v-dialog v-model="detailDialog" persistent scrollable max-width="820px">
            <v-card v-if="selected">
                <v-card-title class="title pa-3 primary white--text">
                    <v-icon class="mr-2 white--text">history</v-icon>
                    {{ formatAction(selected.action) }}

                    <v-spacer></v-spacer>

                    <v-chip x-small label :color="selected.success ? 'success' : 'error'" text-color="white">
                        {{ selected.success ? "Success" : "Failed" }}
                    </v-chip>
                </v-card-title>

                <v-card-text class="pt-4">
                    <v-layout row wrap>
                        <v-flex xs12 sm6>
                            <div class="detail-label">User</div>
                            <div class="detail-value">
                                {{ trim(selected.user_name) || "-" }}
                                <small v-if="selected.user_role">({{ selected.user_role }})</small>
                            </div>
                        </v-flex>

                        <v-flex xs12 sm6>
                            <div class="detail-label">Date</div>
                            <div class="detail-value">
                                {{ selected.created_at_bs || "-" }}
                                <small>{{ formatTime(selected.created_at) }}</small>
                            </div>
                        </v-flex>

                        <v-flex xs12 sm6>
                            <div class="detail-label">Entity</div>
                            <div class="detail-value">{{ selected.entity_type || "-" }}</div>
                        </v-flex>

                        <v-flex xs12 sm6>
                            <div class="detail-label">Request</div>
                            <div class="detail-value">
                                {{ selected.method }} {{ selected.route }}
                                <small>{{ selected.status_code }}</small>
                            </div>
                        </v-flex>

                        <v-flex xs12 v-if="entityIds(selected).length">
                            <div class="detail-label">Affected Records</div>
                            <div class="detail-value">
                                <v-chip v-for="id in entityIds(selected)" :key="id" x-small label outlined
                                    class="mr-1 mb-1">
                                    {{ id }}
                                </v-chip>
                            </div>
                        </v-flex>

                        <v-flex xs12 v-if="selected.error_message">
                            <div class="detail-label">Error</div>
                            <div class="detail-value error--text">{{ selected.error_message }}</div>
                        </v-flex>

                        <v-flex xs12 v-if="hasContent(selected.metadata)">
                            <div class="detail-label">Metadata</div>
                            <pre class="activity-detail">{{ pretty(selected.metadata) }}</pre>
                        </v-flex>

                        <v-flex xs12 sm6 v-if="hasContent(selected.before)">
                            <div class="detail-label">Before</div>
                            <pre class="activity-detail">{{ pretty(selected.before) }}</pre>
                        </v-flex>

                        <v-flex xs12 sm6 v-if="hasContent(selected.after)">
                            <div class="detail-label">After</div>
                            <pre class="activity-detail">{{ pretty(selected.after) }}</pre>
                        </v-flex>

                        <v-flex xs12>
                            <div class="detail-label">Request ID</div>
                            <div class="detail-value detail-muted">
                                {{ selected.request_id || "-" }}
                            </div>

                            <div class="detail-label mt-2">User Agent</div>
                            <div class="detail-value detail-muted">
                                {{ selected.user_agent || "-" }}
                            </div>
                        </v-flex>
                    </v-layout>
                </v-card-text>

                <v-card-actions>
                    <v-spacer></v-spacer>

                    <v-btn color="warning" outlined small @click="detailDialog = false">
                        Close
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
import Mixins from "@/library/Mixins";
import { v2Services } from "@/library/helpers";

const NepaliDate = require("nepali-date-js");
const nd = new NepaliDate();

export default {
    name: "BillingActivityLog",

    mixins: [Mixins],

    data: () => ({
        records: [],
        loading: false,
        requestInitialized: false,

        frmDate:
            nd.getYear() +
            "-" +
            nd.getMonth().pad() +
            "-" +
            nd.getDate().pad(),

        toDate: nd.format("YYYY-MM-DD"),

        search: "",

        totalItems: 0,

        detailDialog: false,
        selected: null,

        pagination: {
            page: 1,
            itemsPerPage: 50,
            sortBy: [],
            sortDesc: [],
        },

        headers: [
            {
                text: "#",
                align: "center",
                value: "sn",
                width: 60,
                sortable: false,
            },
            {
                text: "Date",
                align: "left",
                value: "created_at_bs",
                width: 130,
                sortable: false,
            },
            {
                text: "User",
                align: "left",
                value: "user_name",
                width: 180,
                sortable: false,
            },
            {
                text: "Action",
                align: "left",
                value: "action",
                width: 160,
                sortable: false,
            },
            {
                text: "Entity",
                align: "left",
                value: "entity_type",
                width: 230,
                sortable: false,
            },
            {
                text: "Amount",
                align: "right",
                value: "amount",
                width: 130,
                sortable: false,
            },
            {
                text: "Status",
                align: "center",
                value: "success",
                width: 110,
                sortable: false,
            },
            {
                text: "Remarks",
                align: "left",
                value: "remarks",
                width: 340,
                sortable: false,
            },
            {
                text: "IP Address",
                align: "left",
                value: "ip_address",
                width: 140,
                sortable: false,
            },
            {
                text: "Detail",
                align: "center",
                value: "detail",
                width: 90,
                sortable: false,
            },
        ],
    }),

    computed: {
        failedCount() {
            return this.records.filter((item) => !item.success).length;
        },
    },

    watch: {
        pagination: {
            deep: true,
            handler(newValue, oldValue) {
                if (!this.requestInitialized) {
                    this.requestInitialized = true;
                    this.get();
                    return;
                }

                if (!oldValue) {
                    return;
                }

                const pageChanged = newValue.page !== oldValue.page;
                const perPageChanged =
                    newValue.itemsPerPage !== oldValue.itemsPerPage;

                if (pageChanged || perPageChanged) {
                    this.get();
                }
            },
        },
    },

    methods: {
        searchLogs() {
            if (this.pagination.page !== 1) {
                this.pagination.page = 1;
                return;
            }

            this.get();
        },

        get() {
            const params = new URLSearchParams();

            params.append("from", this.frmDate || "");
            params.append("to", this.toDate || "");
            params.append("search", this.search || "");
            params.append("page", this.pagination.page || 1);
            params.append("perPage", this.pagination.itemsPerPage || 50);

            this.loading = true;
            this.records = [];
            this.totalItems = 0;

            this.$restV2(v2Services.BILLING)
                .get(`/v2/activity-logs?${params.toString()}`)
                .then((response) => {
                    const payload = response.data || {};

                    // The endpoint answers with a bare array; the wrapped shape
                    // is handled too in case it gains a meta block later.
                    const records = Array.isArray(payload)
                        ? payload
                        : Array.isArray(payload.data)
                            ? payload.data
                            : [];

                    this.records = records;
                    this.totalItems =
                        !Array.isArray(payload) && payload.meta
                            ? Number(payload.meta.total || 0)
                            : records.length;
                })
                .catch(() => {
                    this.records = [];
                    this.totalItems = 0;
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        viewDetails(item) {
            this.selected = item;
            this.detailDialog = true;
        },

        /**
         * Actions are namespaced, eg. invoice.cancel, so the trailing verb is
         * what decides the colour.
         */
        actionColor(action) {
            const verb = String(action).split(".").pop().toLowerCase();

            const colors = {
                cancel: "error",
                delete: "error",
                rollback: "warning",
                correction: "warning",
                create: "success",
                generate: "success",
                pay: "success",
                update: "primary",
            };

            return colors[verb] || "grey darken-1";
        },

        formatAction(action) {
            if (!action) {
                return "-";
            }

            return String(action)
                .split(/[._-]/)
                .filter(Boolean)
                .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                .join(" ");
        },

        entityIds(item) {
            if (!item || !item.entity_id) {
                return [];
            }

            return String(item.entity_id)
                .split(",")
                .map((id) => id.trim())
                .filter(Boolean);
        },

        remarks(item) {
            const metadata = item.metadata || {};

            return (
                metadata.message ||
                metadata.reason ||
                item.error_message ||
                ""
            );
        },

        hasContent(value) {
            return Boolean(value) && Object.keys(value).length > 0;
        },

        pretty(value) {
            return JSON.stringify(value, null, 2);
        },

        formatTime(value) {
            if (!value) {
                return "";
            }

            const date = new Date(value);

            if (Number.isNaN(date.getTime())) {
                return "";
            }

            return date.toLocaleTimeString("en-US", {
                hour: "2-digit",
                minute: "2-digit",
                hour12: true,
            });
        },

        trim(value) {
            return value ? String(value).trim() : "";
        },

        currency(value) {
            const number = parseFloat(value);

            return new Intl.NumberFormat("en-NP", {
                style: "currency",
                currency: "NPR",
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            }).format(Number.isNaN(number) ? 0 : number);
        },

        rowNumber(index) {
            const page = this.pagination.page || 1;
            const perPage = this.pagination.itemsPerPage || 50;
            return (page - 1) * perPage + index + 1;
        },
    },
};
</script>

<style lang="scss" scoped>
.activity-log-table {
    width: 100%;
}

.activity-log-table ::v-deep .v-data-table__wrapper {
    overflow-x: auto;
}

.activity-log-table ::v-deep table {
    min-width: 1770px;
}

.activity-log-table ::v-deep th {
    white-space: nowrap;
    font-weight: 700;
}

.activity-log-table ::v-deep td {
    white-space: nowrap;
}

.activity-log-table ::v-deep .cell-subtitle {
    display: block;
    margin-top: 2px;
    color: #888;
    font-weight: 400;
}

.activity-log-table ::v-deep .log-remarks {
    white-space: normal;
    line-height: 1.35;
}

.activity-log-table ::v-deep .failed-row td:first-child {
    border-left: 3px solid #ff5252 !important;
}

.detail-label {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: #999;
    margin-bottom: 2px;
}

.detail-value {
    font-size: 14px;
    margin-bottom: 12px;
    word-break: break-word;
}

.detail-value small {
    color: #888;
}

.detail-muted {
    font-size: 12px;
    color: #888;
}

.activity-detail {
    background-color: #fafafa;
    border: 1px solid #eee;
    border-radius: 4px;
    padding: 10px;
    margin-bottom: 12px;
    max-height: 260px;
    overflow: auto;
    font-size: 12px;
    white-space: pre-wrap;
    word-break: break-word;
}
</style>
